/* eslint-disable react-hooks/exhaustive-deps */
import CloseIcon from "@mui/icons-material/Close";
import { FormHelperText } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import axios from "axios";
import { useEffect, useState } from "react";
import { API_URL } from "../constants/config";
import { useUser } from "../contexts/UserContext";
import { convertPlnToUsd } from "../utils/converter";
import { errorAlert } from "../utils/toast";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 400 },
  bgcolor: "background.paper",
  border: "1px solid #ddd",
  boxShadow: 24,
  borderRadius: "8px",
  overflow: "hidden",
  maxHeight: { xs: "90vh", sm: "none" },
  display: "flex",
  flexDirection: "column",
};

const Spinner = () => (
  <svg
    className="animate-spin h-5 w-5 mr-3 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0114.32-4.32A5 5 0 009 16.5A5 5 0 004 12z"
    />
  </svg>
);

const Payment = (props) => {
  const { user } = useUser();
  const { open, handleClose } = props;

  const [reportCount, setReportCount] = useState(1);
  const [reportPrice] = useState(17);
  const [totalAmount, setTotalAmount] = useState(reportPrice);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoMessage, setPromoMessage] = useState(null);
  const [error, setError] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(totalAmount);
  const [isLoading, setIsLoading] = useState(false);
  const [discount, setDiscount] = useState(0);

  const applyPromoCode = async (reportCount) => {
    // Changed parameter to reportCount
    if (!promoCode.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/promo-codes/validate`,
        {
          code: promoCode.trim(),
          report_count: reportCount, // Changed from amount to report_count
          user_id: user.id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("Promo Code Response:", response.data);

      if (response.status >= 200 && response.status < 300) {
        setPromoMessage("Promo code applied successfully!");
        const finalAmount = Number(
          response.data.data.final_amount || totalAmount
        );
        const discountAmount = Number(response.data.data.discount || 0);
        setDiscountedPrice(isNaN(finalAmount) ? totalAmount : finalAmount);
        setDiscount(isNaN(discountAmount) ? 0 : discountAmount);
      } else {
        setError(response.data.message || "Invalid promo code.");
        setDiscountedPrice(totalAmount);
        setDiscount(0);
        setPromoMessage(null);
      }
    } catch (error) {
      console.error("Promo Code Error:", error.response?.data || error);
      // Handle validation errors from Laravel
      if (error.response?.status === 422 && error.response.data.message) {
        const errors = error.response.data.message;
        // Extract the first error message from the object
        const firstError = Object.values(errors)[0][0];
        setError(firstError);
      } else {
        setError(error.response?.data?.message || "Error applying promo code.");
      }
      setDiscountedPrice(totalAmount);
      setDiscount(0);
      setPromoMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const newTotal = Number(reportCount) * Number(reportPrice);
    const validTotal = isNaN(newTotal) ? reportPrice : newTotal;
    setTotalAmount(validTotal);
    setDiscountedPrice(validTotal);

    if (promoCode.trim() && promoMessage) {
      applyPromoCode(reportCount); // Pass reportCount instead of validTotal
    }
  }, [reportCount, reportPrice]);

  const handlePaymentClick = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const finalAmount = promoMessage ? discountedPrice : totalAmount;

      if (finalAmount < 1) {
        errorAlert("Amount must be greater than 0");
        setIsSubmitting(false);
        return;
      }

      const data = {
        amount: finalAmount,
        currency: "PLN",
        promo_code: promoCode,
        report_count: reportCount,
        original_amount: totalAmount,
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/stripe/create-session`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { url } = response.data.data;
      setTimeout(() => {
        setIsSubmitting(false);
        window.location.href = url;
      }, 2000);
    } catch (error) {
      console.error("Payment Error:", error);
      errorAlert("Payment initialization error from server!");
      setIsSubmitting(false);
    }
  };

  const handleReportCountChange = (e) => {
    const value = e.target.value;

    // Allow empty input temporarily (for better UX)
    if (value === "") {
      setReportCount("");
      return;
    }

    const numericValue = parseInt(value, 10);

    // Ensure the value is greater than 0
    if (!isNaN(numericValue) && numericValue > 0) {
      setReportCount(numericValue);
    } else {
      errorAlert("Number of reports must be greater than 0.");
      setReportCount(1);
    }
  };

  const handlePromoCodeChange = (e) => {
    setPromoCode(e.target.value);
    setError(null);
    if (!e.target.value.trim()) {
      setPromoMessage(null);
      setDiscount(0);
      setDiscountedPrice(totalAmount);
    }
  };

  const handleClearPromoCode = () => {
    setPromoCode("");
    setPromoMessage(null);
    setDiscount(0);
    setDiscountedPrice(totalAmount);
    setError(null);
  };

  const handleApplyPromoCode = () => {
    if (isLoading || !promoCode.trim()) {
      setError("Please enter a valid promo code.");
      return;
    }

    if (reportCount <= 0) {
      // Updated check to use reportCount
      setError("Number of reports must be greater than 0.");
      return;
    }

    applyPromoCode(reportCount); // Pass reportCount instead of totalAmount
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <div className="flex items-center justify-between p-4 border-b bg-blue-50">
          <p className="font-bold text-lg text-blue-800">
            Choose Payment Method
          </p>
          <button
            className="text-gray-500 hover:text-gray-700 transition-colors"
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          <form id="order" className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Number of Reports <span className="text-red-500">*</span>
              </label>
              <input
                required
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                name="reportCount"
                value={reportCount}
                min="1"
                type="number"
                onChange={handleReportCountChange}
              />
            </div>

            <div className="mt-4">
              <label className="block text-gray-700 font-medium mb-2">
                Have a Promo Code?
              </label>
              <div className="relative flex justify-between items-center gap-2">
                <input
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all pr-10"
                  placeholder="Enter code"
                  type="text"
                  value={promoCode}
                  onChange={handlePromoCodeChange}
                />
                {promoCode && (
                  <button
                    type="button"
                    className="absolute right-28 text-gray-500 hover:text-gray-700 transition-colors"
                    onClick={handleClearPromoCode}
                    aria-label="Clear promo code"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
                <button
                  type="button"
                  className="px-4 py-2 text-sm border border-cf--primary text-cf--primary bg-white min-w-[90px] rounded-md transition-all hover:text-white font-bold hover:bg-cf--primary disabled:opacity-50"
                  onClick={handleApplyPromoCode}
                  disabled={isLoading}
                >
                  {isLoading ? "Applying..." : "Apply"}
                </button>
              </div>

              {promoMessage && (
                <div className="mt-2 bg-green-100 text-green-800 p-2 rounded-md flex items-center">
                  <span className="mr-2">✔️</span>
                  <FormHelperText sx={{ fontWeight: "bold", margin: 0 }}>
                    {promoMessage}
                  </FormHelperText>
                </div>
              )}
              {error && (
                <div className="mt-2 bg-red-100 text-red-800 p-2 rounded-md flex items-center">
                  <span className="mr-2">❌</span>
                  <FormHelperText sx={{ fontWeight: "bold", margin: 0 }}>
                    {error}
                  </FormHelperText>
                </div>
              )}
            </div>

            <div
              className={`bg-blue-50 p-4 rounded-lg mt-4 border ${
                promoMessage ? "border-green-200" : "border-blue-100"
              }`}
            >
              <h3 className="font-bold mb-2">Payment Summary</h3>
              <div className="flex justify-between text-sm mb-1">
                <span>Price per report:</span>
                <span>
                  {Number(reportPrice).toFixed(2)} PLN ($
                  {convertPlnToUsd(reportPrice)})
                </span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>Number of reports:</span>
                <span>{reportCount}</span>
              </div>
              <div className="flex justify-between text-sm mb-2 pb-2 border-b border-blue-200">
                <span>Original amount:</span>
                <span>
                  {Number(totalAmount).toFixed(2)} PLN ($
                  {convertPlnToUsd(totalAmount)})
                </span>
              </div>
              {promoMessage && Number(discount) > 0 && (
                <div className="flex justify-between text-sm mb-2 text-green-700">
                  <span>Discount applied:</span>
                  <span>
                    -{Number(discount).toFixed(2)} PLN ($-
                    {convertPlnToUsd(discount)})
                  </span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-1">
                <span>Final Amount:</span>
                <span>
                  {Number(promoMessage ? discountedPrice : totalAmount).toFixed(
                    2
                  )}{" "}
                  PLN ($
                  {convertPlnToUsd(
                    promoMessage ? discountedPrice : totalAmount
                  )}
                  )
                </span>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="button"
                className="w-full rounded-md px-4 py-3 bg-cf--primary text-white font-bold transition-all hover:bg-cf--secondary shadow-sm disabled:opacity-50"
                disabled={isSubmitting}
                onClick={handlePaymentClick}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <Spinner />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Pay with Card or Blik"
                )}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default Payment;
